/**
 * Cliente do **ARGINK Bridge** (nome técnico do projeto open source: `open-icp-bridge`) — o
 * agente local do ARGINK para certificados ICP-Brasil (P03, D-113; provider ÚNICO, D-116).
 *
 * O Bridge é um projeto próprio (MIT, repositório `open-icp-bridge`): um programa instalado
 * na máquina do signatário, que é quem toca o provider do sistema operacional. Este módulo é
 * o ÚNICO ponto do signer-ui que conhece esse protocolo.
 *
 * ## Dois transportes, um só Bridge
 *
 * - **loopback HTTP** (`lib/bridge-loopback.ts`) — `127.0.0.1`, portas 9987 → 9988 → 9989.
 *   É o caminho PREFERENCIAL e a superfície do produto: não depende de nada publicado em
 *   loja, então quem instala o `.exe` volta para a página já aberta e ela detecta sozinha.
 * - **MV3/Native Messaging** (`window.openIcpBridge`) — LEGADO/fallback, não é requisito de
 *   nada. Só é consultado quando o programa não está escutando em loopback.
 *
 * Os dois entregam as MESMAS mensagens v1 ao MESMO dispatcher, com o mesmo consentimento
 * nativo. `resolveBridgeApi` é o único lugar que decide por qual deles se fala.
 *
 * ## Provider único (D-116)
 *
 * Não existe segundo caminho. O agente Java de `apps/local-agent` saiu da jornada de produto:
 * não é detectado, não lista, não autoriza e não assina. `lib/agent.ts` ficou sendo apenas o
 * vocabulário compartilhado (`AgentCert`, `AgentError`) que ESTE módulo produz a partir das
 * respostas do Bridge. Bridge ausente conduz à instalação — nunca a um fallback que o
 * mascare (`lib/signing-provider.ts`, `src/__tests__/provider-unico.test.ts`).
 *
 * ## Invariante (D-001, reafirmada aqui)
 *
 * ZERO custódia de chave privada de terceiro. Este módulo nunca recebe, pede, deriva,
 * serializa ou registra chave privada, PIN ou senha de token — o protocolo do Bridge
 * não os expõe, e o consentimento acontece num diálogo NATIVO, fora do alcance desta
 * página. O que trafega são metadados públicos, o DTBS que o backend emitiu e a
 * assinatura resultante.
 *
 * ## Confiança: o Bridge é conferido, não acreditado (D-113)
 *
 * O Bridge roda na máquina do signatário e não é autoridade sobre a cerimônia. Toda
 * resposta dele que o ARGINK for USAR passa por verificação independente aqui:
 *
 * - o `id` de um certificado é `sha256hex(DER)` por contrato → `get-certificate` só é
 *   aceito se o DER recebido de fato tem esse hash (`assertCertificateDerMatchesId`);
 * - `sign` só é aceito se ecoou o mesmo `certificateId`, o mesmo par de algoritmos, se
 *   `signedDigestSha256` bate com `sha256hex(SHA-256(dtbs))` calculado localmente, e se
 *   o `certificateDer` devolvido é byte-a-byte o mesmo enviado ao backend na `cert_chain`.
 *
 * Divergência em qualquer um desses aborta ANTES de `completeSession` — nunca se
 * "completa mesmo assim". A autoridade final continua sendo o servidor, que revalida
 * sessão, vínculo do certificado (D-035) e a própria assinatura sobre o DTBS que ele
 * mesmo emitiu.
 */
import { AgentError, type AgentCert } from './agent';
import { connectLoopbackBridge } from './bridge-loopback';
import { sha256Base64, sha256HexOfBase64 } from './crypto';
import { newTraceId } from './trace';

/** Versão do protocolo do Bridge que este cliente sabe falar. */
export const SUPPORTED_BRIDGE_PROTOCOL = 1;

/** Comandos sem os quais a Qualificada não roda — checados no `version`, não presumidos. */
const REQUIRED_COMMANDS = ['list-certificates', 'get-certificate', 'sign'] as const;

/** O par que o ARGINK usa hoje: `RSA_SHA256` no `completeSession` = PKCS#1 v1.5 + SHA-256. */
const SIGNATURE_ALGORITHM = 'RSASSA-PKCS1-v1_5';
const HASH_ALGORITHM = 'SHA-256';
/** Rótulo do algoritmo no contrato da API do ARGINK (`CompleteSessionBody.algorithm`). */
export const ARGINK_ALGORITHM = 'RSA_SHA256';

/** Espera pela injeção de `window.openIcpBridge` (content script roda em `document_start`). */
const API_WAIT_TIMEOUT_MS = 3_000;
const API_WAIT_POLL_MS = 100;
/** `version` não abre diálogo nenhum — falha rápido é melhor UX que espera longa. */
const QUICK_TIMEOUT_MS = 10_000;

/**
 * `list-certificates` e `get-certificate` ABREM DIÁLOGO NATIVO e esperam uma pessoa responder.
 *
 * Isto aqui já disse o contrário, e o comentário errado custou uma jornada inteira: medido no Gate B de
 * 09/09/2026, com o Bridge recém-instalado e a página esperando, a listagem morreu em
 * `ERR_ACTION_TIMEOUT` ("não respondeu a tempo") enquanto a janela **"ARGINK Bridge — mostrar
 * certificados"** estava aberta na tela do usuário, esperando o clique dele. Dez segundos é menos do que
 * uma pessoa leva para notar que o Windows abriu um diálogo atrás do navegador.
 *
 * A causa está no host: em modo browser, o `BridgeDispatcher` submete `list-certificates` e
 * `get-certificate` ao mesmo `DiscoveryGate` com consentimento (OIB-A01) — divulgar quais certificados
 * alguém tem é revelar CPF, nome e emissor a um site, e por isso é decisão da pessoa, não da página.
 * O teto do próprio diálogo é 90s (`WindowsConsentProvider.TimeoutSeconds`), mais até 120s de fila
 * quando outro diálogo está aberto. O cliente tem de acomodar isso; desistir antes deixa a UI dizendo
 * "não respondeu" com a pergunta ainda na tela — e o usuário sem entender o que fez de errado.
 */
const CONSENT_TIMEOUT_MS = 180_000;
/** `sign` espera o usuário responder ao diálogo nativo e, em A3, digitar o PIN no middleware. */
const SIGN_TIMEOUT_MS = 180_000;

// ---------------------------------------------------------------------------
// Protocolo (docs/PROTOCOL.md do repositório open-icp-bridge, v1)
// ---------------------------------------------------------------------------

export interface BridgeResponse<T> {
  protocolVersion: number;
  requestId: string;
  ok: boolean;
  result?: T;
  error?: { code?: string; message?: string };
}

export interface BridgeVersionResult {
  name: string;
  version: string;
  sourceRevision?: string;
  protocolVersion: number;
  supportedProtocolVersions?: number[];
  platform?: string;
  providers?: string[];
  commands?: string[];
  signatureAlgorithms?: string[];
  hashAlgorithms?: string[];
}

/** Metadado PÚBLICO de certificado. Por contrato nunca traz chave, PIN ou handle do SO. */
export interface BridgeCertificateDescriptor {
  id: string;
  provider: string;
  subject: string;
  subjectCommonName?: string;
  issuer: string;
  validFrom: string;
  validTo: string;
  isTimeValid: boolean;
  hasPrivateKey: boolean;
  keyAlgorithm: 'RSA' | 'EC' | string;
  keySize?: number;
  keyProvider?: { name?: string; api?: 'capi' | 'cng'; storage?: 'software' | 'smartcard' | 'unknown' };
  icpBrasil?: {
    isLikelyIcpBrasil?: boolean;
    classification?: string;
    policyOid?: string;
    certificateType?: string;
  };
}

export interface BridgeSignResult {
  certificateId: string;
  signatureAlgorithm: string;
  hashAlgorithm: string;
  signature: string;
  signedDigestSha256: string;
  certificateDer: string;
}

/**
 * A superfície do Bridge, comum aos dois transportes: o loopback HTTP a implementa em
 * `lib/bridge-loopback.ts`, e a extensão MV3 a injeta no MAIN world.
 */
export interface OpenIcpBridgeApi {
  protocolVersion: number;
  /** Qual carro trouxe a mensagem. Ausente = extensão (o transporte legado, que não se anuncia). */
  transport?: 'loopback' | 'extension';
  isExtensionPresent: () => boolean;
  ping: () => Promise<BridgeResponse<unknown>>;
  version: () => Promise<BridgeResponse<BridgeVersionResult>>;
  listCertificates: (args?: { filter?: string; requirePrivateKey?: boolean }) => Promise<
    BridgeResponse<{ certificates?: BridgeCertificateDescriptor[] }>
  >;
  getCertificate: (certificateId: string) => Promise<BridgeResponse<{ certificateDer?: string }>>;
  sign: (
    args: Record<string, unknown>,
    opts?: { timeoutMs?: number },
  ) => Promise<BridgeResponse<BridgeSignResult>>;
}

declare global {
  interface Window {
    openIcpBridge?: OpenIcpBridgeApi;
  }
}

// ---------------------------------------------------------------------------
// Detecção e capabilities
// ---------------------------------------------------------------------------

/**
 * Estado REAL da ponte, com os casos separados porque a ação do usuário difere em
 * cada um. Colapsar tudo em "não encontrado" é o que hoje faz o `AgentPanel` do
 * caminho Java mandar "abra o aplicativo" para quem nunca instalou nada.
 */
export type BridgeStatus =
  /** Bridge alcançável, protocolo e capabilities compatíveis. */
  | { kind: 'ready'; version: string; platform: string | null; providers: string[] }
  /** Nenhum transporte alcança o Bridge — o usuário precisa INSTALAR o programa. */
  | { kind: 'bridge_absent' }
  /** Transporte alcançado, mas o programa não respondeu — instalação incompleta ou removida. */
  | { kind: 'host_absent' }
  /** Os dois vivos, mas esta versão do Bridge não serve o que a Qualificada exige. */
  | { kind: 'incompatible'; version: string | null; reason: BridgeIncompatibilityReason };

export type BridgeIncompatibilityReason =
  | 'protocol_version'
  | 'missing_command'
  | 'missing_signature_algorithm'
  | 'missing_hash_algorithm';

/**
 * Resolve o transporte pelo qual esta página alcança o Bridge, **loopback primeiro**.
 *
 * A ordem não é preferência estética: o loopback HTTP é a superfície do produto porque não
 * depende de nada publicado em loja — o usuário instala um `.exe` e a página, já aberta,
 * passa a enxergar o Bridge. A extensão MV3 fica como caminho legado, e só é consultada
 * quando o programa não está escutando em 127.0.0.1.
 *
 * Nunca bloqueia a tela: a varredura das três portas tem teto próprio e a espera pela
 * injeção da extensão é curta e limitada.
 */
async function resolveBridgeApi(timeoutMs = API_WAIT_TIMEOUT_MS): Promise<OpenIcpBridgeApi | null> {
  const loopback = await connectLoopbackBridge();
  if (loopback) return loopback;
  return waitForExtensionApi(timeoutMs);
}

/**
 * Espera `window.openIcpBridge` — a extensão injeta em `document_start`, mas o React
 * pode montar antes de o script da página ter corrido. Espera curta e limitada.
 */
async function waitForExtensionApi(timeoutMs = API_WAIT_TIMEOUT_MS): Promise<OpenIcpBridgeApi | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const api = window.openIcpBridge;
    if (api) return api;
    if (Date.now() >= deadline) return null;
    await new Promise((resolve) => setTimeout(resolve, API_WAIT_POLL_MS));
  }
}

/**
 * Prende uma chamada ao Bridge a um teto de tempo e a um resultado, sempre.
 *
 * Nada aqui pode ficar pendurado: quem espera é a tela do signatário, e uma promessa que
 * nunca assenta deixa a etapa de certificado presa em "procurando" para sempre. A API é
 * injetada por uma extensão — código de terceiro no MAIN world da página — então este
 * cliente não pode presumir que ela sempre resolve, nem que só rejeita com `Error`.
 *
 * Uma rejeição vira resposta `ok:false` com o código dado, para que o caminho de erro seja
 * um só: o mesmo `bridgeError`/`describeError` que o resto do módulo usa. Estourar o tempo
 * é tratado como o Bridge não estar respondendo — que é exatamente o que o usuário precisa
 * saber, e o que mantém a política fail-closed.
 */
function withTimeout<T>(
  operation: Promise<BridgeResponse<T>>,
  timeoutMs: number,
  timeoutCode: string,
  timeoutMessage: string,
): Promise<BridgeResponse<T>> {
  const failure = (code: string, message: string): BridgeResponse<T> => ({
    protocolVersion: SUPPORTED_BRIDGE_PROTOCOL,
    requestId: '',
    ok: false,
    error: { code, message },
  });
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(failure(timeoutCode, timeoutMessage)), timeoutMs);
    operation.then(
      (response) => {
        clearTimeout(timer);
        resolve(response);
      },
      (err) => {
        clearTimeout(timer);
        resolve(failure('ERR_EXTENSION_UNAVAILABLE', err instanceof Error ? err.message : String(err)));
      },
    );
  });
}

/** `true` sse algum transporte alcança o Bridge nesta máquina (sem conferir capabilities). */
export async function isBridgeReachable(): Promise<boolean> {
  const api = await resolveBridgeApi();
  return api !== null && api.isExtensionPresent();
}

function versionSupportsProtocol(result: BridgeVersionResult): boolean {
  const served = result.supportedProtocolVersions ?? [result.protocolVersion];
  return served.includes(SUPPORTED_BRIDGE_PROTOCOL);
}

/**
 * Descobre o estado da ponte por **contrato explícito** (`version`), não por inferência:
 * o Bridge declara protocolo, comandos e algoritmos, e este cliente confere se o que a
 * Qualificada precisa está lá. Uma versão futura que deixe de servir `sign` com
 * PKCS#1 v1.5 é `incompatible` aqui — nunca uma falha obscura no meio da assinatura.
 */
export async function detectBridge(): Promise<BridgeStatus> {
  const api = await resolveBridgeApi();
  if (!api || !api.isExtensionPresent()) return { kind: 'bridge_absent' };

  const response = await withTimeout(
    api.version(),
    QUICK_TIMEOUT_MS,
    'ERR_NATIVE_UNAVAILABLE',
    'o ARGINK Bridge não respondeu a tempo',
  );
  if (!response.ok || !response.result) {
    // `ERR_NATIVE_UNAVAILABLE` é o código da própria extensão para "host não instalado
    // ou não registrado". Qualquer outra falha aqui também significa, na prática, que o
    // programa não está respondendo — o usuário tem a mesma ação a tomar.
    return { kind: 'host_absent' };
  }

  const result = response.result;
  const version = result.version ?? null;
  if (!versionSupportsProtocol(result)) {
    return { kind: 'incompatible', version, reason: 'protocol_version' };
  }
  const commands = result.commands ?? [];
  if (!REQUIRED_COMMANDS.every((command) => commands.includes(command))) {
    return { kind: 'incompatible', version, reason: 'missing_command' };
  }
  if (!(result.signatureAlgorithms ?? []).includes(SIGNATURE_ALGORITHM)) {
    return { kind: 'incompatible', version, reason: 'missing_signature_algorithm' };
  }
  if (!(result.hashAlgorithms ?? []).includes(HASH_ALGORITHM)) {
    return { kind: 'incompatible', version, reason: 'missing_hash_algorithm' };
  }
  return {
    kind: 'ready',
    version: result.version,
    platform: result.platform ?? null,
    providers: result.providers ?? [],
  };
}

// ---------------------------------------------------------------------------
// Erros
// ---------------------------------------------------------------------------

/**
 * Traduz o erro do Bridge para o `AgentError` que `lib/errors.ts` já sabe humanizar.
 *
 * Reusar o tipo é deliberado: o signatário não deve ver duas famílias de mensagem
 * para o mesmo problema só porque o transporte por baixo mudou. O código original do
 * Bridge vai em `body.error`, onde `codigoSeguro` de `errors.ts` o aceita (tem forma
 * de código) e o log técnico o preserva.
 */
function bridgeError(code: string | undefined, message: string, traceId: string): AgentError {
  const kind = (() => {
    switch (code) {
      case 'ERR_NATIVE_UNAVAILABLE':
      case 'ERR_EXTENSION_UNAVAILABLE':
        return 'agent_unreachable' as const;
      case 'ERR_ACTION_TIMEOUT':
        return 'timeout' as const;
      // A janela ABRIU e ninguém respondeu. Não é o Bridge que está lento: é o diálogo nativo
      // esperando uma pessoa. Medido no Gate B de 09/09/2026 — a tela dizia "o ARGINK Bridge não
      // respondeu a tempo" e a única ação que destravava (responder à janela do Windows, que fica
      // atrás do navegador) não aparecia em lugar nenhum.
      case 'ERR_CONSENT_TIMEOUT':
        return 'consent_timeout' as const;
      case 'ERR_ORIGIN_REQUIRED':
      case 'ERR_ORIGIN_INVALID':
      case 'ERR_ORIGIN_NOT_ALLOWED':
        return 'origin_blocked' as const;
      case 'ERR_CONSENT_DENIED':
        return 'auth_denied' as const;
      default:
        return 'response' as const;
    }
  })();
  return new AgentError(kind, message, traceId, null, { error: code, message });
}

function unwrap<T>(response: BridgeResponse<T>, fallbackMessage: string, traceId: string): T {
  if (!response.ok || response.result === undefined) {
    throw bridgeError(response.error?.code, response.error?.message ?? fallbackMessage, traceId);
  }
  return response.result;
}

async function requireApi(traceId: string): Promise<OpenIcpBridgeApi> {
  const api = await resolveBridgeApi();
  if (!api) {
    throw bridgeError('ERR_EXTENSION_UNAVAILABLE', 'o ARGINK Bridge não está disponível neste computador', traceId);
  }
  return api;
}

// ---------------------------------------------------------------------------
// Descoberta de certificados
// ---------------------------------------------------------------------------

/**
 * Rótulo de origem no vocabulário que a UI já usa (`format.sourceLabel`: `'p12'` =
 * "A1 (arquivo .p12/.pfx)", `'mscapi'` = "A3 (token/smartcard)").
 *
 * A classificação vem do **tipo ICP-Brasil declarado no certificado** (arco de OID de
 * política `2.16.76.1.2.*`), não da origem técnica: pelo Bridge todo certificado
 * chega pelo repositório do Windows, então herdar o rótulo do transporte diria "A3"
 * para um A1 em software. Quando o tipo não é conhecido, `keyProvider.storage` decide,
 * e o desempate final é `'p12'` — o caso comum, e o rótulo que não promete hardware
 * que talvez não exista.
 */
function sourceForDescriptor(descriptor: BridgeCertificateDescriptor): AgentCert['source'] {
  const type = descriptor.icpBrasil?.certificateType;
  if (type === 'A1' || type === 'A2' || type === 'S1' || type === 'S2') return 'p12';
  if (type === 'A3' || type === 'A4' || type === 'S3' || type === 'S4') return 'mscapi';
  return descriptor.keyProvider?.storage === 'smartcard' ? 'mscapi' : 'p12';
}

/**
 * `CertificateDescriptor` do Bridge → `AgentCert`, o tipo que `CertList`, `lib/cpf.ts`
 * e `SignPage` já consomem.
 *
 * O ponto que faz a integração inteira caber: `subject` é preservado no formato
 * RFC2253, que é exatamente o que `extractCertCpf` lê para casar o titular com a
 * expectativa da cerimônia (#92). O filtro de identidade não precisou de uma linha.
 *
 * `certChainB64` sai VAZIO de propósito: `list-certificates` devolve só metadado, e o
 * DER é buscado sob demanda em `fetchBridgeCertChain`, apenas para o certificado que o
 * usuário escolheu. Baixar a cadeia de todos os certificados para montar uma lista
 * seria trabalho e exposição sem uso.
 */
export function toAgentCert(descriptor: BridgeCertificateDescriptor): AgentCert {
  return {
    keyId: descriptor.id,
    label: descriptor.subjectCommonName ?? descriptor.subject,
    subject: descriptor.subject,
    issuer: descriptor.issuer,
    notAfter: descriptor.validTo,
    source: sourceForDescriptor(descriptor),
    certChainB64: [],
  };
}

/**
 * `true` sse o certificado pode ser usado numa assinatura Qualificada por este caminho.
 *
 * Fail-closed e ANTES da tela: um certificado sem chave utilizável nesta máquina, ou
 * com chave que o `completeSession` do ARGINK não sabe verificar (hoje só RSA, via
 * `RSA_SHA256`), não é oferecido — em vez de ser oferecido e falhar depois do usuário
 * ter escolhido e digitado o PIN.
 *
 * Certificado VENCIDO continua passando: D-034 fixou que vencimento é aviso na UI
 * (`CertList` marca "Vencido") e recusa do SERVIDOR, não decisão do cliente.
 */
export function isUsableForQualified(descriptor: BridgeCertificateDescriptor): boolean {
  return descriptor.hasPrivateKey && descriptor.keyAlgorithm === 'RSA';
}

/**
 * Certificados ICP-Brasil desta máquina, já normalizados e restritos ao que serve para
 * a Qualificada. Só metadados: nenhuma chave é aberta, nenhum PIN é pedido, nenhum cartão é
 * acionado — descobrir NÃO é usar.
 *
 * Mas **exige consentimento**: o Bridge abre um diálogo nativo perguntando se esta página pode
 * ver os certificados (OIB-A01), porque a lista carrega nome, CPF e emissor. A chamada só assenta
 * quando a pessoa responde — daí `CONSENT_TIMEOUT_MS`, não `QUICK_TIMEOUT_MS`.
 */
export async function listBridgeCerts(traceId: string = newTraceId()): Promise<AgentCert[]> {
  const api = await requireApi(traceId);
  const response = await withTimeout(
    api.listCertificates({ filter: 'icp-brasil', requirePrivateKey: true }),
    CONSENT_TIMEOUT_MS,
    'ERR_ACTION_TIMEOUT',
    'a leitura dos certificados não respondeu a tempo',
  );
  const result = unwrap(response, 'não foi possível listar os certificados desta máquina', traceId);
  return (result.certificates ?? []).filter(isUsableForQualified).map(toAgentCert);
}

/**
 * Confere que o DER recebido é mesmo o do certificado pedido: por contrato do
 * protocolo, `id` é `sha256hex(DER)`. É uma verificação independente — o hash é
 * recalculado aqui, sobre os bytes que de fato chegaram.
 */
async function assertCertificateDerMatchesId(
  certificateDer: string,
  expectedId: string,
  traceId: string,
): Promise<void> {
  const actualId = await sha256HexOfBase64(certificateDer);
  if (actualId !== expectedId.toLowerCase()) {
    throw bridgeError(
      'ERR_CERTIFICATE_MISMATCH',
      'o certificado devolvido não corresponde ao que foi escolhido',
      traceId,
    );
  }
}

/**
 * Cadeia a enviar em `openSession`. O Bridge devolve a folha; o backend documenta que
 * **só o índice 0 importa** para o vínculo (`domain/certificate-binding.ts`), então uma
 * cadeia de um elemento é o contrato completo, não uma simplificação.
 */
export async function fetchBridgeCertChain(
  certificateId: string,
  traceId: string = newTraceId(),
): Promise<string[]> {
  const api = await requireApi(traceId);
  const response = await withTimeout(
    api.getCertificate(certificateId),
    CONSENT_TIMEOUT_MS,
    'ERR_ACTION_TIMEOUT',
    'a leitura do certificado escolhido não respondeu a tempo',
  );
  const result = unwrap(response, 'não foi possível ler o certificado escolhido', traceId);
  const der = result.certificateDer;
  if (!der) {
    throw bridgeError('ERR_INVALID_RESPONSE', 'o certificado escolhido veio sem conteúdo', traceId);
  }
  await assertCertificateDerMatchesId(der, certificateId, traceId);
  return [der];
}

// ---------------------------------------------------------------------------
// Assinatura
// ---------------------------------------------------------------------------

export interface BridgeSignature {
  signatureValueB64: string;
  algorithm: string;
  certChainB64: string[];
}

export interface BridgeSignInput {
  certificateId: string;
  /** O DTBS emitido por `openSession` (Base64), assinado byte-a-byte como veio. */
  dtbsB64: string;
  /** O DER (Base64) já enviado ao backend em `cert_chain` — conferido contra a resposta. */
  expectedCertificateDer: string;
  /** Contexto mostrado ao usuário no diálogo NATIVO (≤120 chars, sem jargão de infra). */
  purpose: string;
}

/**
 * Assina o DTBS da cerimônia no provider local, através do Bridge.
 *
 * O consentimento é etapa fixa do pipeline do host (origem → validação → certificado →
 * capacidade da chave → **consentimento** → provider → guarda de saída) e acontece num
 * diálogo nativo: nem esta página nem o backend do ARGINK conseguem pular, pré-aprovar
 * ou responder por ele. Em A3 o PIN é pedido pelo middleware do fabricante, num passo
 * que este código não vê e não intermedeia.
 *
 * `dataToSign.type = 'dtbs'`: o Bridge computa o SHA-256 e assina. O protocolo declara
 * essa forma como a paridade com o Local Agent (`dtbs_b64` + `RSA_SHA256`), e com
 * PKCS#1 v1.5 o próprio sistema operacional embrulha o digest em DigestInfo — que é
 * exatamente o que `signerInfo.signature` de CMS/PAdES espera.
 *
 * Os quatro gates de D-113 rodam sobre a resposta ANTES de ela virar `completeSession`.
 */
export async function signWithBridge(
  input: BridgeSignInput,
  traceId: string = newTraceId(),
): Promise<BridgeSignature> {
  const api = await requireApi(traceId);
  // O teto do `sign` é largo de propósito: entre o pedido e a resposta cabem o diálogo
  // nativo de consentimento e, num A3, o PIN digitado no middleware do fabricante.
  const response = await withTimeout(
    api.sign(
      {
        certificateId: input.certificateId,
        signatureAlgorithm: SIGNATURE_ALGORITHM,
        hashAlgorithm: HASH_ALGORITHM,
        dataToSign: { type: 'dtbs', value: input.dtbsB64 },
        purpose: input.purpose.slice(0, 120),
      },
      { timeoutMs: SIGN_TIMEOUT_MS },
    ),
    SIGN_TIMEOUT_MS + 5_000,
    'ERR_ACTION_TIMEOUT',
    'a assinatura não foi concluída a tempo neste computador',
  );
  const result = unwrap(response, 'a assinatura não pôde ser concluída neste computador', traceId);

  // Gate 1 — a assinatura é do certificado que o usuário escolheu, e não de outro.
  if (result.certificateId !== input.certificateId) {
    throw bridgeError('ERR_CERTIFICATE_MISMATCH', 'a assinatura veio de um certificado diferente do escolhido', traceId);
  }

  // Gate 2 — os algoritmos são os que o `completeSession` vai declarar como RSA_SHA256.
  if (result.signatureAlgorithm !== SIGNATURE_ALGORITHM || result.hashAlgorithm !== HASH_ALGORITHM) {
    throw bridgeError('ERR_ALGORITHM_MISMATCH', 'a assinatura usou um algoritmo diferente do combinado', traceId);
  }

  // Gate 3 — o que foi assinado é o DTBS desta sessão. `signedDigestSha256` é o sha256
  // hex do digest assinado; com `type: 'dtbs'` esse digest é SHA-256(dtbs), então o
  // valor esperado é recalculável aqui sem depender de nada que o Bridge afirme.
  const expectedSignedDigest = await sha256HexOfBase64(await sha256Base64(input.dtbsB64));
  if (result.signedDigestSha256?.toLowerCase() !== expectedSignedDigest) {
    throw bridgeError('ERR_DTBS_MISMATCH', 'o conteúdo assinado não corresponde a este documento', traceId);
  }

  // Gate 4 — o certificado da assinatura é byte-a-byte o que o backend recebeu na
  // `cert_chain` e sobre o qual fez o vínculo com o signatário (D-035).
  if (result.certificateDer !== input.expectedCertificateDer) {
    throw bridgeError('ERR_CERTIFICATE_MISMATCH', 'o certificado da assinatura mudou durante a operação', traceId);
  }

  if (!result.signature) {
    throw bridgeError('ERR_INVALID_RESPONSE', 'a operação terminou sem valor de assinatura', traceId);
  }

  return {
    signatureValueB64: result.signature,
    algorithm: ARGINK_ALGORITHM,
    certChainB64: [result.certificateDer],
  };
}
